import { useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { COMMAND_EXAMPLES } from '../prompt/commands';
import type { ExecutionStatus, WorkspaceTab } from '../prompt/executor';
import { MAX_PROMPT_LENGTH, parsePrompt } from '../prompt/parser';
import { useCommandRunner } from '../state/useCommandRunner';
import { Button, Panel, inputClass } from './ui';

interface HistoryEntry {
  id: number;
  prompt: string;
  status: ExecutionStatus;
  /** What took effect (kept even when a later step failed). */
  applied: string[];
  error?: string;
  skipped: string[];
  suggestions: string[];
}

const ENTRY_STYLES: Record<ExecutionStatus, string> = {
  success: 'border-emerald-900 bg-emerald-950/40 text-emerald-200',
  partial: 'border-amber-800 bg-amber-950/40 text-amber-200',
  failed: 'border-red-900 bg-red-950/40 text-red-200',
};

export function PromptPanel({ navigate }: { navigate: (tab: WorkspaceTab) => void }) {
  const runCommands = useCommandRunner(navigate);
  const [text, setText] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [running, setRunning] = useState(false);
  const nextId = useRef(1);
  const inputId = useId();
  const helpId = useId();
  const textarea = useRef<HTMLTextAreaElement>(null);

  const push = (entry: Omit<HistoryEntry, 'id'>) =>
    setHistory((h) => [{ ...entry, id: nextId.current++ }, ...h].slice(0, 20));

  // Runs synchronously inside the submit/keydown handler so "play" keeps the user gesture.
  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    const prompt = text.trim();
    const parsed = parsePrompt(prompt);
    if (!parsed.ok) {
      push({
        prompt: prompt || '(empty)',
        status: 'failed',
        applied: [],
        error: parsed.error.message,
        skipped: [],
        suggestions: parsed.error.suggestions,
      });
      return;
    }
    setRunning(true);
    try {
      const result = await runCommands(parsed.commands);
      push({
        prompt,
        status: result.status,
        applied: result.applied,
        error: result.failure?.message,
        skipped: result.skipped,
        suggestions: [],
      });
      // Clear once anything took effect, so re-running cannot double-apply changes.
      if (result.status !== 'failed') setText('');
    } finally {
      setRunning(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const applyExample = (example: string) => {
    setText(example);
    textarea.current?.focus();
  };

  return (
    <Panel title="Prompt" className="flex h-fit flex-col lg:sticky lg:top-4">
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-2">
        <label htmlFor={inputId} className="sr-only">
          Command prompt
        </label>
        <textarea
          id={inputId}
          ref={textarea}
          value={text}
          rows={3}
          maxLength={MAX_PROMPT_LENGTH}
          placeholder='Try "set bpm to 120 and play"'
          aria-describedby={helpId}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          className={`${inputClass} resize-y font-mono`}
          data-testid="prompt-input"
        />
        <div className="flex items-center justify-between gap-2">
          <p id={helpId} className="text-xs text-zinc-400">
            Enter to run · Shift+Enter for a new line. Local commands only — no AI.
          </p>
          <Button type="submit" variant="primary" size="sm" disabled={running} data-testid="prompt-submit">
            Run
          </Button>
        </div>
      </form>

      <div aria-live="polite" aria-label="Prompt results" className="mt-3 flex max-h-72 flex-col gap-2 overflow-y-auto" data-testid="prompt-log">
        {history.map((entry) => (
          <div
            key={entry.id}
            className={`rounded-md border p-2 text-xs ${ENTRY_STYLES[entry.status]}`}
            data-status={entry.status}
          >
            <p className="font-mono text-zinc-300">› {entry.prompt}</p>
            {entry.status === 'partial' && <p className="font-semibold">Partly applied — these changes were kept:</p>}
            {entry.applied.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
            {entry.error && <p className={entry.status === 'partial' ? 'mt-1 font-semibold' : ''}>{entry.error}</p>}
            {entry.skipped.length > 0 && (
              <p className="text-zinc-400">Not run: {entry.skipped.join(', ')}</p>
            )}
            {entry.status === 'failed' && entry.applied.length === 0 && <p className="text-zinc-400">Nothing was changed.</p>}
            {entry.suggestions.length > 0 && (
              <p className="mt-1 text-zinc-400">
                Try:{' '}
                {entry.suggestions.map((s, i) => (
                  <span key={s}>
                    {i > 0 && ', '}
                    <button type="button" className="text-accent-300 underline" onClick={() => applyExample(s)}>
                      {s}
                    </button>
                  </span>
                ))}
              </p>
            )}
          </div>
        ))}
      </div>

      <details className="mt-3 text-xs text-zinc-400">
        <summary className="cursor-pointer text-zinc-300">Supported commands</summary>
        <ul className="mt-2 flex flex-col gap-1.5">
          {COMMAND_EXAMPLES.map((c) => (
            <li key={c.example}>
              <button type="button" className="font-mono text-accent-300 hover:underline" onClick={() => applyExample(c.example)}>
                {c.example}
              </button>
              <span className="block text-zinc-400">{c.description}</span>
            </li>
          ))}
        </ul>
      </details>
    </Panel>
  );
}
